import type { JSX, ReactNode } from 'react'

/**
 * ══ NHÃN TRẠNG THÁI · THANH TAB · LỐI TẮT MISA ══════════════════════════════════════════════════
 * Chép `proship-trans/src/components/ui.tsx:1010 · 1303 · 1380` (G2).
 */

/** `ui.tsx:1010`. Bản cũ dùng 84 lần trong phạm vi RT. */
export const Badge = (
  { children, className = '', size = 'md', title }:
  { children: ReactNode; className?: string; size?: 'md' | 'sm'; title?: string },
): JSX.Element => {
  const sizes: Record<'md' | 'sm', string> = {
    md: 'px-2.5 py-0.5 text-xs',
    sm: 'px-1.5 py-0 text-[10px]',
  }
  return (
    <span title={title} className={`inline-flex items-center rounded-full font-semibold ${sizes[size]} ${className}`}>
      {children}
    </span>
  )
}

/**
 * `ui.tsx:1303`. Bản cũ dùng 134 lần trong phạm vi RT.
 *
 * ⭐ THÊM `role="tab"` — LỆCH BẢN CŨ CÓ CHỦ ĐÍCH, và là lệch DUY NHẤT của component này.
 * Bản cũ render `<button type="button">` TRẦN, không vai, không thuộc tính. Hệ quả đo được ở G0:
 * mọi bộ chọn `[role="tab"]` trả **0 kết quả trên mọi màn**, nên `capture.ts` phải dò tab theo
 * HÌNH DẠNG (nút bo góc đứng cạnh nhau) — một phép dò đoán mò, và nó đã đoán nhầm nhiều lần.
 * rs-erp đã dùng `role="tab"` sẵn ở `catalog.tsx`, nên giữ nó ở đây là cho hai bên nhất quán.
 *
 * KHÔNG đổi một pixel: `role` là thuộc tính ngữ nghĩa, không có biểu hiện thị giác. Cùng loại
 * ngoại lệ với `aria-current` mà `AppLayout` đã thêm (xem chú thích `NavLeaf`) — §7 bảo vệ *tầng
 * người dùng NHÌN THẤY và thao tác*, còn đây là tầng trình đọc màn hình và máy đo đọc.
 */
export const TabPill = (
  { active, onClick, children, tone = 'brand', dataAttr, title }:
  {
    active: boolean
    onClick: () => void
    children: ReactNode
    tone?: 'brand' | 'sky'
    /**
     * Chú giải hiện khi rê chuột.
     *
     * ⛔ CÓ LỐI TRUYỀN NÀY ĐỂ KHÔNG AI PHẢI BỌC `<span title>` QUANH TAB. `/az/pricing` từng bọc
     * như vậy, và hệ quả là LƯỚI ĐO đọc màn đó ra **0 tab**: nó nhận ra thanh tab bằng hình
     * dạng — "phần tử có từ HAI NÚT CON TRỰC TIẾP trở lên" — mà một tab bị bọc thì không còn là
     * con trực tiếp. Đặt `title` thẳng lên nút cũng đúng hơn về trợ năng: trình đọc màn hình gắn
     * chú giải vào chính thứ bấm được, không vào cái hộp quanh nó.
     */
    title?: string
    /**
     * Mốc bám cho ca kiểm, render thành `data-<tên>="<giá trị>"`.
     *
     * ⚠ KHÔNG CÓ Ở BẢN CŨ, và cố ý vẫn thêm. Lý do: `catalog.tsx` · `ops-catalog.tsx` ·
     * `ContractsModal.tsx` đã gắn `data-tab` / `data-ops-kind` / `data-amis-kind` từ trước, và
     * **ảnh đối chiếu lẫn E2E bám theo chúng** (`a-baseline-screenshots.spec.ts` tìm
     * `[data-tab="customers"]`). Không có lối truyền này thì hoặc ba màn đó không dùng được
     * `TabPill` — tức giữ nguyên tab tự chế, đúng thứ G2 sinh ra để xoá — hoặc phải sửa ca kiểm
     * cho vừa mã, điều §8 cấm.
     * Thuộc tính `data-*` không có biểu hiện thị giác nên KHÔNG đổi một pixel.
     */
    dataAttr?: { name: string; value: string }
  },
): JSX.Element => {
  const activeCls = tone === 'sky' ? 'bg-sky-600 text-white shadow' : 'bg-brand-500 text-white shadow'
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      {...(dataAttr === undefined ? {} : { [`data-${dataAttr.name}`]: dataAttr.value })}
      {...(title === undefined ? {} : { title })}
      onClick={onClick}
      className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition whitespace-nowrap ${active ? activeCls : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
    >
      {children}
    </button>
  )
}

/**
 * `ui.tsx:1380`. Icon nhỏ mở thẳng MISA AMIS (tab mới) — user chốt 22/08, ảnh logo THẬT do user
 * cung cấp. App KHÔNG đấu API/SSO, đây chỉ là LỐI TẮT sang trang MISA thật, kế toán vẫn tự đăng
 * nhập bên đó. DÙNG CHUNG cho Dashboard + Kế toán (QuanTri) — sửa 1 chỗ, cả 2 trang tự đổi theo.
 *
 * ⚠ ẢNH ĐI BẰNG ĐƯỜNG DẪN TĨNH, KHÔNG nhúng `data:` — lệch bản cũ có chủ đích, cùng lý do với
 *   quyết định F11 §4 (logo báo giá) đã duyệt. Bản cũ nhúng chuỗi base64 **≈12 KB** thẳng vào mã;
 *   ở rs-erp `@rs/ui` nằm TRONG gói khởi tạo, mà gói đó chỉ còn **12,76 kB** dư địa (§9 kế hoạch)
 *   ⇒ nhúng vào là vỡ cổng `ci:budget` vì đúng một cái icon. Ảnh in ra y hệt, chỉ đổi đường lấy.
 */
export const MisaAmisIcon = (): JSX.Element => (
  <a
    href="https://actapp.misa.vn/app"
    target="_blank"
    rel="noreferrer"
    title="Mở MISA AMIS"
    className="w-9 h-9 rounded-full shadow-sm ring-1 ring-black/5 hover:scale-110 transition-transform shrink-0 overflow-hidden bg-white grid place-items-center"
  >
    <img src="/misa-amis.png" alt="MISA AMIS" className="w-full h-full object-cover" />
  </a>
)
